import {
  Bell,
  Building2,
  CheckCircle2,
  ClipboardList,
  Flag,
  RefreshCcw,
  Save,
  Send,
  UserCog,
} from 'lucide-react';
import type { ChangeEvent, FormEvent, ReactNode } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Spinner,
} from '@helios/ui';
import { settingsControlClass } from '@/features/settings/settingsChrome';
import type {
  FeatureFlagItem,
  NotificationChannel,
  NotificationItem,
  OrganizationSettings,
  UserSettings,
} from '@/services/settingsClient';

export type UserForm = UserSettings['preferences'] & {
  name: string;
  title: string;
};

export type OrganizationForm = OrganizationSettings['settings'] & {
  name: string;
};

export const selectClassName = settingsControlClass;

export function UserPreferencesForm({
  form,
  onChange,
  onSubmit,
  saving,
}: {
  form: UserForm | null;
  onChange: (form: UserForm) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  saving: boolean;
}) {
  if (!form) {
    return <SettingsCardEmpty title="User preferences" description="No current user settings were returned." />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UserCog className="h-4 w-4 text-primary" aria-hidden="true" />
          Profile
        </CardTitle>
        <CardDescription>Name, title, and notification preferences for the signed-in user.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={onSubmit}>
          <Field label="Name">
            <Input value={form.name} onChange={(event) => onChange({ ...form, name: event.target.value })} />
          </Field>
          <Field label="Title">
            <Input value={form.title} onChange={(event) => onChange({ ...form, title: event.target.value })} />
          </Field>
          <Field label="Timezone">
            <Input value={form.timezone} onChange={(event) => onChange({ ...form, timezone: event.target.value })} />
          </Field>
          <Field label="Date format">
            <Input value={form.dateFormat} onChange={(event) => onChange({ ...form, dateFormat: event.target.value })} />
          </Field>
          <Field label="Landing page">
            <Input value={form.landingPage} onChange={(event) => onChange({ ...form, landingPage: event.target.value })} />
          </Field>
          <div className="grid gap-3">
            <Toggle
              label="Dense mode"
              checked={form.denseMode}
              onChange={(checked) => onChange({ ...form, denseMode: checked })}
            />
            <Toggle
              label="Email digest"
              checked={form.emailDigestEnabled}
              onChange={(checked) => onChange({ ...form, emailDigestEnabled: checked })}
            />
            <Toggle
              label="Critical only"
              checked={form.criticalOnlyNotifications}
              onChange={(checked) => onChange({ ...form, criticalOnlyNotifications: checked })}
            />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? <Spinner className="h-4 w-4" aria-hidden="true" /> : <Save className="h-4 w-4" aria-hidden="true" />}
              Save Preferences
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function OrganizationSettingsForm({
  form,
  onChange,
  onSubmit,
  saving,
}: {
  form: OrganizationForm | null;
  onChange: (form: OrganizationForm) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  saving: boolean;
}) {
  if (!form) {
    return <SettingsCardEmpty title="Organization settings" description="No tenant organization was returned." />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="h-4 w-4 text-primary" aria-hidden="true" />
          Organization
        </CardTitle>
        <CardDescription>Company-level controls applied inside the current tenant boundary.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={onSubmit}>
          <Field label="Company name">
            <Input value={form.name} onChange={(event) => onChange({ ...form, name: event.target.value })} />
          </Field>
          <Field label="Timezone">
            <Input value={form.timezone} onChange={(event) => onChange({ ...form, timezone: event.target.value })} />
          </Field>
          <Field label="Fiscal year start">
            <Input
              type="number"
              min={1}
              max={12}
              value={form.fiscalYearStartMonth}
              onChange={(event) => onChange({ ...form, fiscalYearStartMonth: Number(event.target.value) })}
            />
          </Field>
          <Field label="Audit retention years">
            <Input
              type="number"
              min={1}
              max={25}
              value={form.auditRetentionYears}
              onChange={(event) => onChange({ ...form, auditRetentionYears: Number(event.target.value) })}
            />
          </Field>
          <Field label="Default notification channel">
            <select
              className={selectClassName}
              value={form.defaultNotificationChannel}
              onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                onChange({ ...form, defaultNotificationChannel: event.target.value as NotificationChannel })
              }
            >
              <option value="IN_APP">In app</option>
              <option value="EMAIL">Email</option>
              <option value="SMS">SMS</option>
              <option value="WEBHOOK">Webhook</option>
            </select>
          </Field>
          <div className="grid gap-3">
            <Toggle
              label="Require MFA"
              checked={form.requireMfa}
              onChange={(checked) => onChange({ ...form, requireMfa: checked })}
            />
            <Toggle
              label="External sharing"
              checked={form.allowExternalSharing}
              onChange={(checked) => onChange({ ...form, allowExternalSharing: checked })}
            />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? <Spinner className="h-4 w-4" aria-hidden="true" /> : <Save className="h-4 w-4" aria-hidden="true" />}
              Save Organization
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function FeatureFlagPanel({
  flags,
  rollouts,
  onRolloutChange,
  onToggle,
  onSaveRollout,
  pendingKey,
  saving,
}: {
  flags: FeatureFlagItem[];
  rollouts: Record<string, number>;
  onRolloutChange: (key: string, value: number) => void;
  onToggle: (flag: FeatureFlagItem) => void;
  onSaveRollout: (flag: FeatureFlagItem) => void;
  pendingKey: string | null;
  saving: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Flag className="h-4 w-4 text-primary" aria-hidden="true" />
          Feature flags
        </CardTitle>
        <CardDescription>Tenant-scoped rollout controls.</CardDescription>
      </CardHeader>
      <CardContent>
        {flags.length === 0 ? (
          <EmptyState title="No feature flags" description="Seed S03 data to populate tenant feature flags." />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full min-w-[760px] border-collapse text-left text-sm">
              <thead className="bg-card text-2xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="border-b border-border px-3 py-2 font-medium">Flag</th>
                  <th className="border-b border-border px-3 py-2 font-medium">Category</th>
                  <th className="border-b border-border px-3 py-2 font-medium">Rollout</th>
                  <th className="border-b border-border px-3 py-2 font-medium">Status</th>
                  <th className="border-b border-border px-3 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {flags.map((flag) => {
                  const rowSaving = saving && pendingKey === flag.key;
                  return (
                    <tr key={flag.id} className="border-b border-border/70 last:border-b-0">
                      <td className="px-3 py-3">
                        <p className="font-medium text-foreground">{flag.name}</p>
                        <p className="mt-1 max-w-xl text-xs text-muted-foreground">{flag.description ?? flag.key}</p>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground">{flag.category}</td>
                      <td className="px-3 py-3">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={rollouts[flag.key] ?? flag.rolloutPercent}
                          disabled={flag.locked}
                          onChange={(event) => onRolloutChange(flag.key, Number(event.target.value))}
                          endAdornment={<span className="text-xs">%</span>}
                          className="w-28"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <Badge variant={flag.enabled ? 'success' : 'outline'}>
                          {flag.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                        {flag.locked ? <Badge variant="warning" className="ml-2">Locked</Badge> : null}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" size="xs" variant="outline" disabled={flag.locked || rowSaving} onClick={() => onToggle(flag)}>
                            {rowSaving ? <Spinner className="h-3.5 w-3.5" aria-hidden="true" /> : null}
                            {flag.enabled ? 'Disable' : 'Enable'}
                          </Button>
                          <Button type="button" size="xs" variant="ghost" disabled={flag.locked || rowSaving} onClick={() => onSaveRollout(flag)}>
                            Save rollout
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function NotificationsPanel({
  items,
  loading,
  onRefresh,
  onMarkAllRead,
  onCreateTest,
  onMarkRead,
  busy,
}: {
  items: NotificationItem[];
  loading: boolean;
  onRefresh: () => void;
  onMarkAllRead: () => void;
  onCreateTest: () => void;
  onMarkRead: (id: string) => void;
  busy: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Bell className="h-4 w-4 text-primary" aria-hidden="true" />
              Notifications
            </CardTitle>
            <CardDescription>In-app notifications scoped to the current user and tenant.</CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="xs" variant="outline" disabled={busy} onClick={onCreateTest}>
              <Send className="h-3.5 w-3.5" aria-hidden="true" />
              Test
            </Button>
            <Button type="button" size="xs" variant="ghost" disabled={busy} onClick={onMarkAllRead}>
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              Mark all read
            </Button>
            <Button type="button" size="xs" variant="ghost" onClick={onRefresh}>
              <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <LoadingRow label="Loading notifications…" />
        ) : items.length === 0 ? (
          <EmptyState title="No notifications" description="Send a test notification to verify the in-app channel." />
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
            {items.map((item) => (
              <li key={item.id} className="flex items-start justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <Badge variant={notificationVariant(item.type)}>{formatNotificationType(item.type)}</Badge>
                    <span className="text-xs text-muted-foreground">{formatTime(item.createdAt)}</span>
                    {item.isRead ? <Badge variant="outline">Read</Badge> : null}
                  </div>
                  <p className="font-medium text-foreground">{item.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{item.body}</p>
                </div>
                <Button type="button" size="xs" variant="outline" disabled={item.isRead || busy} onClick={() => onMarkRead(item.id)}>
                  Mark read
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function AuditTrailPanel({
  items,
  loading,
  page,
  totalPages,
  canPrevious,
  canNext,
  onPrevious,
  onNext,
  onRefresh,
  filters,
}: {
  items: Array<{
    id: string;
    action: string;
    entityType: string;
    actor: { name: string; email: string } | null;
    requestId: string | null;
    createdAt: string;
  }>;
  loading: boolean;
  page: number;
  totalPages: number;
  canPrevious: boolean;
  canNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onRefresh: () => void;
  filters?: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList className="h-4 w-4 text-primary" aria-hidden="true" />
              Audit log
            </CardTitle>
            <CardDescription>Who changed what, and when. Tenant-scoped unless you are a platform admin.</CardDescription>
          </div>
          <Button type="button" size="xs" variant="ghost" onClick={onRefresh}>
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {filters ? <div className="mb-4">{filters}</div> : null}
        {loading ? (
          <LoadingRow label="Loading audit trail…" />
        ) : items.length === 0 ? (
          <EmptyState title="No audit events" description="Mutations that write an audit record will appear here." />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full min-w-[680px] border-collapse text-left text-xs">
              <thead className="bg-secondary/40 text-2xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="border-b border-border px-2 py-1.5 font-medium">Action</th>
                  <th className="border-b border-border px-2 py-1.5 font-medium">Actor</th>
                  <th className="border-b border-border px-2 py-1.5 font-medium">Entity</th>
                  <th className="border-b border-border px-2 py-1.5 font-medium">Time</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-border/70 last:border-b-0">
                    <td className="px-2 py-1.5">
                      <p className="font-medium text-foreground">{item.action}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{item.requestId ?? 'No request id'}</p>
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground">
                      {item.actor ? `${item.actor.name} (${item.actor.email})` : 'System'}
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground">{item.entityType}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{formatTime(item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            Page {page}{totalPages > 0 ? ` of ${totalPages}` : ''}
          </span>
          <div className="flex gap-2">
            <Button type="button" size="xs" variant="outline" disabled={!canPrevious} onClick={onPrevious}>
              Previous
            </Button>
            <Button type="button" size="xs" variant="outline" disabled={!canNext} onClick={onNext}>
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-1 text-xs">
      <span className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex h-7 items-center justify-between gap-3 rounded-md border border-border bg-background/40 px-2.5 text-xs text-foreground">
      <span>{label}</span>
      <input
        type="checkbox"
        className="h-4 w-4 accent-primary"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

export function SettingsCardEmpty({ title, description }: { title: string; description: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <EmptyState title={title} description={description} />
      </CardContent>
    </Card>
  );
}

export function LoadingRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-background/40 px-4 py-3 text-sm text-muted-foreground">
      <Spinner className="h-4 w-4 text-primary" aria-hidden="true" />
      {label}
    </div>
  );
}

export function SettingsStatus({
  message,
  error,
}: {
  message: string | null;
  error: Error | null;
}) {
  if (!message && !error) return null;
  return (
    <div
      className={
        error
          ? 'rounded-lg border border-critical/40 bg-critical/10 px-4 py-3 text-sm text-critical'
          : 'rounded-lg border border-success/40 bg-success/10 px-4 py-3 text-sm text-success'
      }
      role="status"
    >
      {error ? error.message : message}
    </div>
  );
}

export function SettingsLoading({ label }: { label: string }) {
  return (
    <div className="flex min-h-[320px] items-center justify-center">
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4 text-primary" aria-hidden="true" />
        {label}
      </div>
    </div>
  );
}

export function toUserForm(user: UserSettings): UserForm {
  return {
    name: user.name,
    title: user.title ?? '',
    ...user.preferences,
  };
}

export function toOrganizationForm(organization: OrganizationSettings): OrganizationForm {
  return {
    name: organization.name,
    ...organization.settings,
  };
}

export function clampRollout(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

export function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function notificationVariant(type: NotificationItem['type']) {
  if (type === 'CRITICAL') return 'critical';
  if (type === 'WARNING') return 'warning';
  if (type === 'SUCCESS') return 'success';
  return 'info';
}

function formatNotificationType(type: NotificationItem['type']): string {
  return type.toLowerCase().replace(/^\w/, (char) => char.toUpperCase());
}
